import { Center, useDisclosure, useToast } from '@chakra-ui/react';
import { PatchDataWrapper } from '@diamond/shared/data-access';
import { BackofficeOrderDetail } from '@diamond/shared/types';
import { Button, Modal, showToast, Text } from '@diamond/shared/ui';
import { useCrossTabLock } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useActivitiesDetail } from '../../api';
import { useActivityAction } from './action-context';

const CANCEL_ORDER_URL = 'backoffice/order/cancel';

type CancelButtonProps = {
  activitiesId: string;
  queryString: {
    order_by: string;
    direction: string;
    size: number;
    page: number;
  };
};

export function CancelButton({ activitiesId, queryString }: CancelButtonProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const session = useAuthStore();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [inFlight, setInFlight] = useState(false);
  const [waitingForCancelStatus, setWaitingForCancelStatus] = useState(false);
  const waitingCancelTimeoutRef = useRef<number | null>(null);
  const { submitBusy, cancelBusy, setCancelBusy } = useActivityAction();

  const selectHeader = useCallback(
    (d: BackofficeOrderDetail) => ({
      status: d.order.status,
      cancel_reason_id: d.order.cancel_reason_id,
    }),
    []
  );
  const { activitiesDetailData: header } = useActivitiesDetail(
    activitiesId,
    queryString,
    { select: selectHeader }
  );

  const { patchData: cancelOrder } = PatchDataWrapper(
    CANCEL_ORDER_URL,
    session.access_token,
    'admin'
  );

  const {
    key: cancelKey,
    locked: cancelLocked,
    isOwner: cancelIsOwner,
    acquire: acquireCancel,
    release: releaseCancel,
  } = useCrossTabLock('order-cancel', activitiesId, {
    ttlMs: 5000,
    heartbeatMs: 1500,
  });

  // Read peer lock (submit) to cross-disable safely
  const { locked: submitLocked, isOwner: submitIsOwner } = useCrossTabLock(
    'order-submit',
    activitiesId,
    { ttlMs: 5000, heartbeatMs: 1500 }
  );

  const extendCancelCooldown = (ms: number) => {
    try {
      const now = Date.now();
      let ownerTabId = 'cooldown';
      const existing = localStorage.getItem(cancelKey);
      if (existing) {
        try {
          const parsed = JSON.parse(existing) as Partial<{
            ownerTabId: string;
          }>;
          if (parsed && typeof parsed.ownerTabId === 'string') {
            ownerTabId = parsed.ownerTabId;
          }
        } catch {
          /* ignore invalid json */
        }
      }
      const payload = {
        ownerTabId,
        ts: now,
        expiresAt: now + ms,
      };
      localStorage.setItem(cancelKey, JSON.stringify(payload));
    } catch {
      /* ignore storage errors */
    }
  };

  // Fallback cooldown for cancel: release after 30s if status didn't change
  useEffect(() => {
    if (!waitingForCancelStatus) return;
    if (waitingCancelTimeoutRef.current) {
      window.clearTimeout(waitingCancelTimeoutRef.current);
    }
    waitingCancelTimeoutRef.current = window.setTimeout(() => {
      setWaitingForCancelStatus(false);
      setCancelBusy(false);
      releaseCancel();
      waitingCancelTimeoutRef.current = null;
    }, 30000);
    return () => {
      if (waitingCancelTimeoutRef.current) {
        window.clearTimeout(waitingCancelTimeoutRef.current);
        waitingCancelTimeoutRef.current = null;
      }
    };
  }, [waitingForCancelStatus, releaseCancel, setCancelBusy]);

  // Release when order is no longer cancellable
  useEffect(() => {
    if (!waitingForCancelStatus) return;
    const cancellable =
      header?.status === 'submitted' ||
      header?.status === 'waiting_for_payment';
    if (!cancellable) {
      setWaitingForCancelStatus(false);
      setCancelBusy(false);
      releaseCancel();
      if (waitingCancelTimeoutRef.current) {
        window.clearTimeout(waitingCancelTimeoutRef.current);
        waitingCancelTimeoutRef.current = null;
      }
    }
  }, [header?.status, waitingForCancelStatus, releaseCancel, setCancelBusy]);

  useEffect(() => {
    return () => {
      if (waitingCancelTimeoutRef.current) {
        window.clearTimeout(waitingCancelTimeoutRef.current);
        waitingCancelTimeoutRef.current = null;
      }
      if (!waitingForCancelStatus) {
        setCancelBusy(false);
        releaseCancel();
      }
    };
  }, [releaseCancel, waitingForCancelStatus, setCancelBusy]);

  const handleOpen = () => onOpen();

  const handleConfirm = async () => {
    if (!header?.cancel_reason_id) {
      showToast(
        toast,
        'error',
        'Alasan pembatalan kosong. Silakan mengisi alasan pembatalan untuk membatalkan pesanan.'
      );
      onClose();
      return;
    }
    if (inFlight || cancelLocked) {
      onClose();
      showToast(toast, 'info', 'Pembatalan sedang diproses pada tab lain.');
      return;
    }
    const gotLock = acquireCancel();
    if (!gotLock) {
      onClose();
      showToast(toast, 'info', 'Pembatalan sedang diproses pada tab lain.');
      return;
    }
    setInFlight(true);
    setCancelBusy(true);
    cancelOrder('', activitiesId)
      .then(async () => {
        showToast(toast, 'success', 'Berhasil Cancel Order');
        setInFlight(false);
        setWaitingForCancelStatus(true);
        extendCancelCooldown(30000);
        navigate('/activities');
      })
      .catch((error: { message?: string }) => {
        showToast(toast, 'error', error?.message || 'Gagal Cancel Order');
        setInFlight(false);
        setCancelBusy(false);
        releaseCancel();
      });
  };

  const peerSubmitLockActive = submitLocked && !submitIsOwner;
  const cancelDisabled =
    !(
      header?.status === 'submitted' || header?.status === 'waiting_for_payment'
    ) ||
    (cancelLocked && !cancelIsOwner) ||
    inFlight ||
    waitingForCancelStatus ||
    cancelBusy ||
    submitBusy ||
    peerSubmitLockActive;

  return (
    <>
      <Button
        px="4"
        borderColor="red"
        color="red"
        onClick={handleOpen}
        isDisabled={cancelDisabled}
        isLoading={cancelBusy}
        aria-busy={cancelBusy}
      >
        Batalkan
      </Button>
      <Modal
        name="cancel_order"
        isOpen={isOpen}
        onClose={onClose}
        labelSubmit="Ya"
        labelCancel="Tidak"
        buttonColor="red"
        onSubmit={handleConfirm}
      >
        <Center>
          <Text m="10" align="center" fontSize="2xl" as="b">
            Cancel Order?
          </Text>
        </Center>
      </Modal>
    </>
  );
}

export default CancelButton;
